import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { resolveCompany } from "@/lib/company-access";

// Global workspace search for the ⌘K command palette: leads, conversations
// and listings in one query, scoped to the caller's company.

export const searchWorkspace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ q: z.string().trim().min(2).max(120) }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await resolveCompany(context.supabase, context.userId);
    if (!access) return { leads: [], conversations: [], listings: [] };

    const like = `%${data.q.replace(/[%_]/g, "")}%`;
    const [leads, conversations, listings] = await Promise.all([
      context.supabase
        .from("leads")
        .select("id, name, email, status")
        .eq("company_id", access.id)
        .or(`name.ilike.${like},email.ilike.${like},question.ilike.${like}`)
        .order("created_at", { ascending: false })
        .limit(5),
      context.supabase
        .from("conversations")
        .select("id, title, visitor_name, source, created_at")
        .eq("company_id", access.id)
        .or(`title.ilike.${like},visitor_name.ilike.${like},visitor_email.ilike.${like}`)
        .order("created_at", { ascending: false })
        .limit(5),
      context.supabase
        .from("listings")
        .select("id, title, type, status")
        .eq("company_id", access.id)
        .ilike("title", like)
        .order("created_at", { ascending: false })
        .limit(5),
    ]);

    return {
      leads: leads.data ?? [],
      conversations: conversations.data ?? [],
      listings: listings.data ?? [],
    };
  });
